import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Button,
  Flex,
  Badge,
  Box,
  Checkbox,
  Text as ChakraText,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Alert,
  AlertDescription,
  AlertIcon,
  Image,
  Tooltip,
  Link,
} from '@chakra-ui/react'
import type { ReactElement } from 'react'
import { useEffect, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { CachePolicies, useFetch } from 'use-http'
import useToast from '../../../hooks/useToast'
import { FeatureNameMapping, FeaturesEnum } from '../../../../utils/features'
import { UserContext } from '../../../providers'

interface Props {
  fileId?: string
  isOpen: boolean
  onClose(): void
  getFileDetails?: () => void
  aiTagData?: AutoTaggingObj[]
  setAiTagData?: (aiTagData: AutoTaggingObj[]) => void
  multipleFile?: boolean
}

interface AutoTaggingObj {
  name: string
  maxTags: number
  minConfidence: number
}

const AutoTagsModal = ({
  fileId,
  isOpen,
  onClose,
  getFileDetails,
  aiTagData,
  setAiTagData,
  multipleFile
}: Props): ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const toast = useToast()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [creditsToBeUsed, updateCreditsToBeUsed] = useState(0)
  const [isGoogleChecked, toggleGoogleCheck] = useState(true)
  const [isAWSChecked, toggleAWSCheck] = useState(true)
  const [confidenceScore, updateConfidenceScore] = useState(70)
  const [maxTags, updateMaxTags] = useState(5)
  const [extensionsArrToUpdate, updateExtensionsArr] = useState<
    AutoTaggingObj[]
  >([])

  useEffect(() => {
    // update credits
    let credits = 0

    if (isAWSChecked) {
      credits += user?.extensionRateCard['aws-auto-tagging'] ?? 0
    }
    if (isGoogleChecked) {
      credits += user?.extensionRateCard['google-auto-tagging'] ?? 0
    }

    updateCreditsToBeUsed(credits)

    // update arr for api
    const extArr: AutoTaggingObj[] = []

    if (isAWSChecked) {
      extArr.push({
        name: 'aws-auto-tagging',
        maxTags,
        minConfidence: confidenceScore,
      })
    }
    if (isGoogleChecked) {
      extArr.push({
        name: 'google-auto-tagging',
        maxTags,
        minConfidence: confidenceScore,
      })
    }
    updateExtensionsArr(extArr)
  }, [isGoogleChecked, isAWSChecked, maxTags, confidenceScore])

  useEffect(() => {
    if (aiTagData?.length) {
      if (aiTagData.map(data => data.name).includes('aws-auto-tagging'))
        toggleAWSCheck(true)
      else toggleAWSCheck(false)
      if (aiTagData.map(data => data.name).includes('google-auto-tagging'))
        toggleGoogleCheck(true)
      else toggleGoogleCheck(false)
      updateMaxTags(aiTagData[0]?.maxTags)
      updateConfidenceScore(aiTagData[0]?.minConfidence)
    }
  }, [aiTagData, isOpen])

  const showAppropriateToast = extensionStatus => {
    if (extensionStatus['aws-auto-tagging'] === 'failed') {
      toast({
        description: t(
          'error_tags_from_aws',
          'Error in applying tags from AWS.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else if (extensionStatus['aws-auto-tagging'] === 'pending') {
      toast({
        description: t(
          'pending_tags_from_aws',
          'Tags from AWS are on their way, will be reflected in your dashboard shortly.',
        ),
        status: 'info',
        position: 'top-right',
      })
    } else if (extensionStatus['aws-auto-tagging'] === 'success') {
      toast({
        description: t(
          'success_tags_from_aws',
          'Successfully applied auto tags from AWS.',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }

    if (extensionStatus['google-auto-tagging'] === 'failed') {
      toast({
        description: t(
          'error_tags_from_google',
          'Error in applying tags from Google.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else if (extensionStatus['google-auto-tagging'] === 'pending') {
      toast({
        description: t(
          'pending_tags_from_google',
          'Tags from Google are on their way, will be reflected in your dashboard shortly.',
        ),
        status: 'info',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else if (extensionStatus['google-auto-tagging'] === 'success') {
      toast({
        description: t(
          'success_tags_from_google',
          'Successfully applied auto tags from Google.',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }

  // const updateAutoTagsArr = details => {
  //   if (
  //     details.extensionStatus['aws-auto-tagging'] === 'success' ||
  //     details.extensionStatus['google-auto-tagging'] === 'success'
  //   ) {
  //     updateAITagsArr(details.AITags)
  //   }
  // }

  const handleSubmit = async () => {
    if (setAiTagData) {
      setAiTagData(extensionsArrToUpdate)
      onClose()
      return
    }
    if (!isAWSChecked && !isGoogleChecked) {
      toast({
        description: t(
          'you_need_to_select_atleast_one_service',
          'You need to select atleast one service',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return
    }

    setIsSubmitting(true)

    const updatedFileDetails = await request.patch(
      `/api/v2/files/${fileId}/details`,
      {
        extensions: extensionsArrToUpdate,
      },
    )

    setIsSubmitting(false)

    if (request.error) {
      toast({
        description:
          updatedFileDetails?.message ??
          t('something_went_wrong', 'Something went wrong!'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return
    }

    onClose()
    if (getFileDetails) void getFileDetails()

    // updateAutoTagsArr(updatedFileDetails)
    showAppropriateToast(updatedFileDetails.extensionStatus)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('auto_tags', 'Auto Tags')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box marginBottom="30px">
            <ChakraText fontWeight="500" fontSize="lg" marginBottom="12px">
              {t('service', 'Service')}
            </ChakraText>
            <Flex>
              <Flex marginRight="30px" alignItems="center">
                <Checkbox
                  isChecked={isGoogleChecked}
                  onChange={e => void toggleGoogleCheck(e.target.checked)}
                />
                <Image
                  boxSize="15px"
                  borderRadius="full"
                  src="https://ik.imagekit.io/ikmedia/google_logo.svg"
                  alt="Google Icon"
                  marginLeft="12px"
                  marginRight="5px"
                />
                <ChakraText>Google</ChakraText>
              </Flex>
              <Flex alignItems="center">
                <Checkbox
                  isChecked={isAWSChecked}
                  onChange={e => void toggleAWSCheck(e.target.checked)}
                />
                <Image
                  boxSize="15px"
                  borderRadius="full"
                  src="https://ik.imagekit.io/ikmedia/tr:h-24,w-24/aws_logo.svg"
                  alt="AWS Icon"
                  marginLeft="12px"
                  marginRight="5px"
                />
                <ChakraText>AWS</ChakraText>
              </Flex>
            </Flex>
          </Box>
          <Box marginBottom="30px">
            <ChakraText fontWeight="500" fontSize="lg" marginBottom="12px">
              {t('confidence_score', 'Confidence Score')}
            </ChakraText>
            <Box>
              <Slider
                focusThumbOnChange={false}
                aria-label="confidence-score"
                min={10}
                max={100}
                defaultValue={confidenceScore}
                onChange={val => void updateConfidenceScore(val)}
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb boxSize="32px" children={confidenceScore} />
              </Slider>
            </Box>
          </Box>
          <Box marginBottom="30px">
            <ChakraText fontWeight="500" fontSize="lg" marginBottom="12px">
              {t('max_tags', 'Max Tags')}
            </ChakraText>
            <Box>
              <Slider
                focusThumbOnChange={false}
                aria-label="max-tags"
                min={1}
                max={30}
                defaultValue={maxTags}
                onChange={val => void updateMaxTags(val)}
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb boxSize="32px" children={maxTags} />
              </Slider>
            </Box>
          </Box>

          {user?.pricingPlan.featuresList.includes(FeaturesEnum.EXTENSIONS) && (
            <Box>
              <Alert status="info" minWidth={0}>
                <AlertIcon />
                {creditsToBeUsed}{' '}
                {creditsToBeUsed === 1
                  ? !multipleFile
                    ? t('credit_will_be_used', 'credit will be used')
                    : t(
                        'credit_will_be_used_per_image',
                        'credit will be used per image',
                      )
                  : !multipleFile
                  ? t('credits_will_be_used', 'credits will be used')
                  : t(
                      'credits_will_be_used_per_image',
                      'credits will be used per image',
                    )}
              </Alert>
            </Box>
          )}

          {!user?.pricingPlan.featuresList.includes(
            FeaturesEnum.EXTENSIONS,
          ) && (
            <Box>
              <Alert status="info" minWidth={0}>
                <AlertIcon />
                <AlertDescription>
                  {FeatureNameMapping[FeaturesEnum.EXTENSIONS]} feature is not
                  available in your current plan.{' '}
                  <Link isExternal href="https://imagekit.io/contact-us/">
                    Contact sales
                  </Link>{' '}
                  to enable this feature.
                </AlertDescription>
              </Alert>
            </Box>
          )}
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button onClick={onClose} variant="outline">
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
            isDisabled={
              isSubmitting ||
              !user?.pricingPlan.featuresList.includes(FeaturesEnum.EXTENSIONS)
            }
            onClick={() => handleSubmit()}
          >
            {aiTagData ? t('save') : t('apply_auto_tags', 'Add auto tags')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AutoTagsModal
